import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, Shield, TrendingUp, Building2, Users, Award,
  CheckCircle2, ChevronRight, Star, Zap, Globe,
  LineChart, Lock, Cpu, Heart, Target, Lightbulb
} from 'lucide-react';
import { servicesService, statsService, testimonialsService, featuresService, certificationsService, settingsService, heroCardService } from '../../services/api';

const iconMap = {
  strategy: Shield,
  wealth: TrendingUp,
  banking: Building2,
  compliance: Lock,
  education: Award,
  support: Cpu,
};

const featureIconMap = {
  award: Award,
  users: Users,
  globe: Globe,
  cpu: Cpu,
  shield: Shield,
  star: Star,
  zap: Zap,
  heart: Heart,
  target: Target,
  lightbulb: Lightbulb,
  check: CheckCircle2,
  lock: Lock,
};

function HomePage() {
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [features, setFeatures] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [settings, setSettings] = useState({});
  const [heroCard, setHeroCard] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [servicesRes, statsRes, testimonialsRes, featuresRes, certificationsRes, settingsRes, heroCardRes] = await Promise.all([
        servicesService.getAll({ featured: 'true' }).catch(() => ({ data: { services: [] } })),
        statsService.getAll().catch(() => ({ data: { stats: [] } })),
        testimonialsService.getAll({ featured: 'true', limit: 3 }).catch(() => ({ data: { testimonials: [] } })),
        featuresService.getAll().catch(() => ({ data: { features: [] } })),
        certificationsService.getAll().catch(() => ({ data: { certifications: [] } })),
        settingsService.getAll().catch(() => ({ data: { settings: {} } })),
        heroCardService.get().catch(() => ({ data: { heroCard: {} } }))
      ]);
      
      setServices(servicesRes.data.services || []);
      setStats(statsRes.data.stats || []);
      setTestimonials(testimonialsRes.data.testimonials || []);
      setFeatures(featuresRes.data.features || []);
      setCertifications(certificationsRes.data.certifications || []);
      setSettings(settingsRes.data.settings || {});
      setHeroCard(heroCardRes.data.heroCard || {});
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  // Récupérer les clients de confiance
  const trustClients = Array.isArray(settings.trust_clients) 
    ? settings.trust_clients 
    : ['Ecobank', 'NSIA', 'Coris Bank', 'BNI'];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section - DYNAMIQUE - LARGEUR OPTIMISÉE */}
<section className="relative min-h-[90vh] flex items-center bg-gradient-to-br from-dark-50 via-white to-primary-50">
  <div className="absolute inset-0 hero-pattern opacity-50" />
  <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-3xl" />
  <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary-500/10 rounded-full blur-3xl" />
  
  {/* Container plus large */}
  <div className="w-full max-w-[1600px] mx-auto px-6 lg:px-12 xl:px-20 relative z-10 py-20">
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
      {/* Colonne gauche - Texte */}
      <motion.div
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        className="lg:pr-8"
      >
        {/* Badge dynamique */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 rounded-full mb-6">
          <Zap className="w-4 h-4 text-primary-600" />
          <span className="text-sm font-medium text-primary-700">
            {settings.hero_badge || 'Leader en transformation digitale financière'}
          </span>
        </div>
        
        {/* Titre dynamique */}
        <h1 className="text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold text-dark-900 leading-tight mb-6">
          {settings.hero_title ? (
            <span dangerouslySetInnerHTML={{ 
              __html: settings.hero_title
                .replace('WealthTech', '<span class="gradient-text">WealthTech</span>')
                .replace('ISO 20022', '<span class="gradient-text">ISO 20022</span>')
            }} />
          ) : (
            <>
              Solutions{' '}
              <span className="gradient-text">WealthTech</span>{' '}
              &{' '}
              <span className="gradient-text">ISO 20022</span>
            </>
          )}
        </h1>
        
        {/* Sous-titre dynamique */}
        <p className="text-lg md:text-xl text-dark-600 mb-8 max-w-2xl">
          {settings.hero_subtitle || 'Votre partenaire de confiance pour la transformation digitale de vos services financiers. Plus de 15 ans d\'expertise au service de votre réussite.'}
        </p>
        
        {/* Boutons dynamiques */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link 
            to={settings.hero_cta_link || '/contact'} 
            className="btn-primary btn-lg group"
          >
            {settings.hero_cta_text || 'Demander un audit gratuit'}
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link 
            to={settings.hero_secondary_link || '/services'} 
            className="btn-secondary btn-lg"
          >
            {settings.hero_secondary_text || 'Découvrir nos services'}
          </Link>
        </div>

        {/* Clients de confiance dynamiques */}
        <div className="mt-12 pt-8 border-t border-dark-200">
          <p className="text-sm text-dark-500 mb-4">Ils nous font confiance</p>
          <div className="flex items-center gap-6 lg:gap-10 opacity-60 flex-wrap">
            {trustClients.map((client, index) => (
              <span key={index} className="font-semibold text-dark-700 text-lg">{client}</span>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Colonne droite - Carte Dashboard DYNAMIQUE */}
      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="relative hidden lg:block lg:pl-8"
      >
        <div className="relative">
          <div className="bg-white rounded-3xl shadow-2xl p-6 xl:p-8 border border-dark-100">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                  <LineChart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-dark-900">{heroCard.title || 'Dashboard Analytics'}</p>
                  <p className="text-sm text-dark-500">{heroCard.subtitle || 'Performance en temps réel'}</p>
                </div>
              </div>
              <span className="badge-secondary">{heroCard.badge_text || 'Live'}</span>
            </div>
            
            <div className="h-48 bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl flex items-end justify-around p-4 mb-6">
              {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ height: 0 }}
                  animate={{ height: `${h}%` }}
                  transition={{ delay: 0.5 + i * 0.1, duration: 0.5 }}
                  className="w-8 bg-gradient-to-t from-primary-500 to-primary-400 rounded-t-lg"
                />
              ))}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-dark-50 rounded-xl">
                <p className="text-2xl font-bold text-primary-600">{heroCard.stat1_value || '98%'}</p>
                <p className="text-xs text-dark-500">{heroCard.stat1_label || 'Uptime'}</p>
              </div>
              <div className="text-center p-3 bg-dark-50 rounded-xl">
                <p className="text-2xl font-bold text-secondary-600">{heroCard.stat2_value || '2.5s'}</p>
                <p className="text-xs text-dark-500">{heroCard.stat2_label || 'Latence'}</p>
              </div>
              <div className="text-center p-3 bg-dark-50 rounded-xl">
                <p className="text-2xl font-bold text-accent-600">{heroCard.stat3_value || '15K'}</p>
                <p className="text-xs text-dark-500">{heroCard.stat3_label || 'Transactions'}</p>
              </div>
            </div>
          </div>

          {/* Certification flottante - DYNAMIQUE */}
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 4, repeat: Infinity }}
            className="absolute -top-6 -right-6 bg-white rounded-2xl shadow-xl p-4 border border-dark-100"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-dark-900">{heroCard.certification_title || 'ISO 20022'}</p>
                <p className="text-sm text-green-600">{heroCard.certification_status || 'Certifié'}</p>
              </div>
            </div>
          </motion.div>

          {/* Bulle flottante - DYNAMIQUE */}
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 5, repeat: Infinity }}
            className="absolute -bottom-4 -left-6 bg-white rounded-2xl shadow-xl p-4 border border-dark-100"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <Globe className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <p className="font-medium text-dark-900">{heroCard.floating_stat_value || '+50 Projets'}</p>
                <p className="text-sm text-dark-500">{heroCard.floating_stat_label || 'En Afrique'}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  </div>
</section>

      {/* Stats Section - DYNAMIQUE */}
      {stats.length > 0 && (
        <section className="py-16 bg-dark-900">
          <div className="container-custom">
            <motion.div 
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid grid-cols-2 md:grid-cols-4 gap-8"
            >
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.id || index}
                  variants={fadeInUp}
                  className="text-center"
                >
                  <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                    {stat.value}
                    <span className="text-primary-400">{stat.suffix}</span>
                  </div>
                  <p className="text-dark-400 font-medium">{stat.label}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Services Section - DYNAMIQUE */}
      {services.length > 0 && (
  <section className="section bg-white">
    <div className="w-full max-w-[1600px] mx-auto px-6 lg:px-12 xl:px-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <span className="badge-primary mb-4">Nos Services</span>
        <h2 className="section-title mb-4">
          Solutions complètes pour votre{' '}
          <span className="gradient-text">transformation digitale</span>
        </h2>
        <p className="section-subtitle mx-auto">
          Des outils adaptés à vos besoins professionnels qui vous apportent 
          plus de visibilité et de services performants.
        </p>
      </motion.div>



            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {services.map((service, index) => {
                const IconComponent = iconMap[service.icon] || Shield;
                return (
                  <motion.div
                    key={service.id || index}
                    variants={fadeInUp}
                  >
                    <Link 
                      to={`/services/${service.slug}`}
                      className="feature-card block h-full group"
                    >
                      <div className="icon-box mb-6 group-hover:scale-110 transition-transform">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <h3 className="text-xl font-semibold text-dark-900 mb-3 group-hover:text-primary-600 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-dark-600 mb-4">
                        {service.short_description}
                      </p>
                      {service.features && (
                        <ul className="space-y-2 mb-6">
                          {service.features.slice(0, 3).map((feature, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-dark-600">
                              <CheckCircle2 className="w-4 h-4 text-secondary-500 flex-shrink-0" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      )}
                      <span className="inline-flex items-center text-primary-600 font-medium group-hover:gap-2 transition-all">
                        En savoir plus
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </span>
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>

            <div className="text-center mt-12">
              <Link to="/services" className="btn-secondary">
                Voir tous nos services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us - DYNAMIQUE */}
      <section className="section bg-dark-50 mesh-bg">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="badge-primary mb-4">Pourquoi nous choisir</span>
              <h2 className="section-title mb-6">
                L'expertise qui fait la{' '}
                <span className="gradient-text">différence</span>
              </h2>
              <p className="text-lg text-dark-600 mb-8">
                {settings.about_text || 'Nous combinons une connaissance approfondie du secteur financier africain avec une expertise technique de pointe pour garantir le succès de vos projets.'}
              </p>

              <div className="space-y-6">
                {features.length > 0 ? (
                  features.map((feature, index) => {
                    const IconComponent = featureIconMap[feature.icon] || Award;
                    return (
                      <motion.div
                        key={feature.id}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="flex gap-4"
                      >
                        <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-soft flex-shrink-0">
                          <IconComponent className="w-6 h-6 text-primary-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-dark-900 mb-1">{feature.title}</h3>
                          <p className="text-dark-600">{feature.description}</p>
                        </div>
                      </motion.div>
                    );
                  })
                ) : (
                  [
                    { icon: Award, title: '15+ ans d\'expérience', desc: 'Une expertise éprouvée dans la transformation digitale bancaire' },
                    { icon: Users, title: 'Équipe certifiée', desc: 'Consultants certifiés sur les principales solutions du marché' },
                    { icon: Globe, title: 'Présence régionale', desc: 'Une connaissance approfondie du marché africain francophone' },
                    { icon: Cpu, title: 'Support 24/7', desc: 'Un accompagnement continu pour assurer votre réussite' },
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="flex gap-4"
                    >
                      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-soft flex-shrink-0">
                        <item.icon className="w-6 h-6 text-primary-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-dark-900 mb-1">{item.title}</h3>
                        <p className="text-dark-600">{item.desc}</p>
                      </div>
                    </motion.div>
                  ))
                )}
              </div>
            </motion.div>

            {/* Certifications - DYNAMIQUE */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              {certifications.length > 0 ? (
                certifications.map((cert) => (
                  <div key={cert.id} className="bg-white rounded-3xl shadow-xl p-8 border border-dark-100">
                    <div className="text-center mb-8">
                      <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary-500/25">
                        <Shield className="w-10 h-10 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-dark-900 mb-2">
                        {cert.title}
                      </h3>
                      <p className="text-dark-600">
                        {cert.subtitle}
                      </p>
                    </div>

                    <div className="space-y-4">
                      {cert.items && cert.items.map((item, i) => (
                        <div key={item.id || i} className="flex items-center gap-3 p-3 bg-dark-50 rounded-xl">
                          <CheckCircle2 className="w-5 h-5 text-secondary-500" />
                          <span className="font-medium text-dark-700">{item.label}</span>
                        </div>
                      ))}
                    </div>

                    <Link to={cert.button_link || '/contact'} className="btn-primary w-full mt-8">
                      {cert.button_text || 'Découvrir'}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Link>
                  </div>
                ))
              ) : (
                <div className="bg-white rounded-3xl shadow-xl p-8 border border-dark-100">
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary-500/25">
                      <Shield className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-dark-900 mb-2">
                      Certification ISO 20022
                    </h3>
                    <p className="text-dark-600">
                      Expertise reconnue sur le standard international
                    </p>
                  </div>

                  <div className="space-y-4">
                    {['Migration complète', 'Tests de conformité', 'Formation équipes', 'Support continu'].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-dark-50 rounded-xl">
                        <CheckCircle2 className="w-5 h-5 text-secondary-500" />
                        <span className="font-medium text-dark-700">{item}</span>
                      </div>
                    ))}
                  </div>

                  <Link to="/services/conseil-iso-20022" className="btn-primary w-full mt-8">
                    Découvrir notre expertise
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Link>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials - DYNAMIQUE */}
      {testimonials.length > 0 && (
        <section className="section bg-white">
          <div className="container-custom">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <span className="badge-primary mb-4">Témoignages</span>
              <h2 className="section-title mb-4">
                Ce que disent{' '}
                <span className="gradient-text">nos clients</span>
              </h2>
              <p className="section-subtitle mx-auto">
                Découvrez les retours d'expérience de nos partenaires qui nous ont fait confiance.
              </p>
            </motion.div>

            <motion.div
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial.id || index}
                  variants={fadeInUp}
                  className="testimonial-card"
                >
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating || 5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-accent-400 text-accent-400" />
                    ))}
                  </div>
                  <p className="text-dark-700 mb-6 leading-relaxed">
                    "{testimonial.testimonial || testimonial.content}"
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-semibold text-lg">
                        {(testimonial.client_name || testimonial.name)?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-dark-900">{testimonial.client_name || testimonial.name}</p>
                      <p className="text-sm text-dark-500">
                        {testimonial.position}{testimonial.company && `, ${testimonial.company}`}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <div className="text-center mt-12">
              <Link to="/temoignages" className="btn-secondary">
                Voir tous les témoignages
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section - DYNAMIQUE */}
      <section className="py-20 relative overflow-hidden" style={{ backgroundColor: 'rgb(26, 50, 84)' }}>
        <div className="absolute inset-0 hero-pattern opacity-10" />

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Prêt à transformer votre institution financière ?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Contactez-nous dès aujourd'hui pour un audit gratuit et découvrez comment nous pouvons vous accompagner.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {settings.contact_email ? (
                <a href={`mailto:${settings.contact_email}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors">
                  {settings.contact_email}
                  <ArrowRight className="w-5 h-5" />
                </a>
              ) : (
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors">
                  Nous contacter
                  <ArrowRight className="w-5 h-5" />
                </Link>
              )}
              {settings.contact_phone ? (
                <a href={`tel:${settings.contact_phone.replace(/\s+/g, '')}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">
                  {settings.contact_phone}
                </a>
              ) : (
                <a href="tel:+2250767811987" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors">
                  Appelez-nous
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default HomePage;